import Autocomplete, {
  AutocompleteInputChangeReason,
  AutocompleteProps
} from '@material-ui/lab/Autocomplete'
import TextField, { TextFieldProps } from '@material-ui/core/TextField'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { withStyles, createStyles } from '@material-ui/styles'
import { InputLabel, useMediaQuery, useTheme } from '@material-ui/core'
import { useDebounce } from 'hooks/useDebounce'
import { AutoFillType } from 'utils/autofill'
import { InputEventHandlers } from 'utils/tracking/amplitude/WithFieldInteraction'
import {
  ReactText,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  memo,
  ChangeEvent
} from 'react'
import { Choice } from 'components/Input/ChoiceGroup'
import { SEPARATOR_VALUE } from 'components/PathSelect'
import TooltipButton from '../../Tooltip/Button'
import Tooltip from '../../Tooltip'

declare global {
  interface Window {
    MSStream: unknown
  }
}
export interface ValueTransformer<T, R = unknown> {
  selector?: keyof T
  extractor?: (raw: T) => R
}

type SelectProps<T, R> = {
  label: ReactText
  error?: string
  tooltip?: string
  clue?: string
  showTooltip?: boolean
  placeholder?: string
  onTooltip?: (isDisplayed: boolean) => void
  value?: string | T
  name?: string
  valueTransformer?: ValueTransformer<T, R>
  optionLabel: keyof T | ((option: T) => string)
  onChange?: (value?: R) => void
  path?: string
  noDebounce?: boolean
  outerLabel?: boolean
  smallTextField?: boolean
  showTooltipOnFieldClick?: boolean
  dependson?: string
} & Omit<
  AutocompleteProps<T, false, false, boolean>,
  'renderInput' | 'onChange' | 'defaultValue' | 'value' | 'onBlur' | 'onFocus'
> &
  Omit<InputEventHandlers<T | null>, 'onChange'>

const SELECT_NO_OPTIONS_TEXT_DEFAULT = 'Keine Ergebnisse'

const STREET_PATHS = [
  'debtors.primary.currentAddress.street',
  'debtors.secondary.currentAddress.street',
  'debtors.primary.previousAddress.street',
  'debtors.secondary.previousAddress.street'
]

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column'
  },
  textFieldContainer: {
    display: 'flex',
    justifyContent: 'center'
  },
  labelStyle: {
    fontSize: '14px',
    color: theme.palette.grey[900],
    marginBottom: '8px'
  }
}))

const StyledAutocomplete = withStyles((theme: Theme) =>
  createStyles({
    listbox: {
      maxHeight: (props) => props.maxheight,
      '& li:hover': {
        backgroundColor: theme.palette.primary[100]
      }
    },
    root: {
      zIndex: 1
    }
  })
)(Autocomplete)

const Select = <T, R>({
  label,
  error,
  tooltip,
  showTooltip = false,
  clue,
  onTooltip,
  noOptionsText = SELECT_NO_OPTIONS_TEXT_DEFAULT,
  placeholder,
  options = [],
  onChange,
  onBlur,
  onFocus,
  onInputChange,
  value,
  name,
  valueTransformer = {},
  optionLabel,
  open = false,
  path,
  noDebounce,
  outerLabel,
  smallTextField,
  showTooltipOnFieldClick,
  dependson,
  autoHighlight,
  className,
  filterOptions,
  freeSolo,
  loading,
  loadingText,
  autoComplete,
  inputValue,
  renderOption
}: SelectProps<T, R>) => {
  const { textFieldContainer, container, labelStyle } = useStyles()

  const { selector, extractor } = valueTransformer
  const [showHint, setHint] = useState<boolean | undefined>(undefined)

  const [isOpen, setOpen] = useState(open)
  const handleOpen = useCallback(
    (wantsToOpen: boolean) => () => {
      setOpen(wantsToOpen)
    },
    [setOpen]
  )

  const [isTooltipDisplayed, setTooltipDisplayed] = useState(showTooltip)
  const onTooltipClick = useCallback(() => {
    const isDisplayed = isOpen ? true : !isTooltipDisplayed

    setTooltipDisplayed(isDisplayed)
    setOpen(false)

    if (onTooltip) {
      onTooltip(isDisplayed)
    }
  }, [isTooltipDisplayed, setTooltipDisplayed, onTooltip, isOpen])

  const inputRef = useRef<HTMLInputElement>(null)

  const inputId =
    label && `text-input-${label.toString().toLowerCase().replace(/\s/g, '')}`

  const isStreetPath = STREET_PATHS.includes(path || '')

  const renderInput = useCallback(
    ({ InputProps, inputProps, ...restTextProps }: TextFieldProps) => {
      const { endAdornment, ...restInputProps } = InputProps!

      return (
        <div className={container}>
          {outerLabel && (
            <InputLabel htmlFor={inputId || ''} classes={{ root: labelStyle }}>
              {label}
            </InputLabel>
          )}
          {tooltip && (
            <Tooltip
              open={showHint}
              isAvailable={showTooltipOnFieldClick}
              isMinimalistic={showTooltipOnFieldClick}
            >
              {tooltip}
            </Tooltip>
          )}
          <div className={textFieldContainer}>
            <TextField
              size={smallTextField ? 'small' : undefined}
              label={!outerLabel && label}
              id={inputId || ''}
              {...restTextProps}
              inputRef={inputRef}
              variant="outlined"
              color="primary"
              error={!!error}
              helperText={(!isStreetPath && error) || clue}
              FormHelperTextProps={{ variant: 'standard' }}
              placeholder={placeholder}
              inputProps={{
                ...inputProps,
                autoComplete: AutoFillType.OFF
              }}
              name={name}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              InputProps={{
                ...restInputProps,
                endAdornment
              }}
            />
            {tooltip && !showTooltipOnFieldClick && (
              <TooltipButton onClick={onTooltipClick} />
            )}
          </div>
        </div>
      )
    },
    [
      container,
      outerLabel,
      inputId,
      labelStyle,
      label,
      tooltip,
      showHint,
      textFieldContainer,
      smallTextField,
      error,
      isStreetPath,
      clue,
      placeholder,
      name,
      showTooltipOnFieldClick,
      onTooltipClick
    ]
  )

  const getOptionSelected = useMemo(
    () =>
      selector
        ? (option: T, currentOption: T) =>
            `${option[selector]}` === `${currentOption[selector]}`
        : (option: T, currentOption: T) => option === currentOption,
    [selector]
  )

  const getOptionLabel = useMemo(
    () =>
      typeof optionLabel === 'function'
        ? optionLabel
        : (option: T) => `${option[optionLabel]}`,
    [optionLabel]
  )

  const renderOptionCallback = useCallback(
    (option: T) => (
      <span
        id={selector ? `${name || 'option'}_${option[selector]}` : undefined}
      >
        {getOptionLabel(option)}
      </span>
    ),
    [getOptionLabel, selector, name]
  )

  const valueSearchFunc = useMemo(
    () =>
      typeof value === 'string' && selector
        ? (option: T) => `${option[selector]}` === `${value}`
        : (option: T) => option === value,
    [selector, value]
  )

  const [selectedValue, setSelectedValue] = useState<T | null>(null)
  const [inputValueState, setInputValueState] = useState('')

  const setValue = useCallback(
    (selected: T | undefined) => {
      if (!selected) {
        return
      }
      setSelectedValue(selected)
      setInputValueState(getOptionLabel(selected))

      // This is used to automatically hide the keyboard after the user selected something
      inputRef.current?.blur()
    },
    [getOptionLabel]
  )

  useEffect(() => {
    setValue(options?.find(valueSearchFunc))
  }, [options, setValue, value, valueSearchFunc])

  const changeHandler = useCallback(
    (_: ChangeEvent<unknown>, choice?: T) => {
      if (choice) {
        setValue(choice)
      }

      if (!onChange) {
        return
      }

      const newValue =
        choice && extractor
          ? extractor(choice)
          : (choice as unknown as R | undefined)

      onChange(newValue)
    },
    [onChange, extractor, setValue]
  )

  const blurHandler = useCallback(() => {
    setTimeout(() => {
      setHint(false)
    }, 300)

    if (onBlur) {
      onBlur(selectedValue)
    }
  }, [onBlur, selectedValue])

  const focusHandler = useCallback(() => {
    setTimeout(() => {
      setHint(Boolean(showTooltipOnFieldClick))
    }, 300)

    if (onFocus) {
      onFocus(selectedValue)
    }
  }, [onFocus, selectedValue, showTooltipOnFieldClick])

  const inputChangeHandler = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      event: ChangeEvent<any>,
      newInput: string,
      reason: AutocompleteInputChangeReason
    ) => {
      setInputValueState(newInput)

      if (onInputChange) {
        onInputChange(event, newInput, reason)
      }
    },
    [setInputValueState, onInputChange]
  )

  const debouncedInputValueState = useDebounce(inputValueState, 200)

  useEffect(() => {
    if (noDebounce) {
      return
    }

    const lcValue = debouncedInputValueState.toLowerCase()
    const matchingOption = options?.find(
      (option) => getOptionLabel(option).toLowerCase() === lcValue
    )

    if (!matchingOption || matchingOption === selectedValue) {
      return
    }

    setSelectedValue(matchingOption)

    if (!onChange) {
      return
    }

    const newValue = extractor
      ? extractor(matchingOption)
      : (matchingOption as unknown as R | undefined)

    onChange(newValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputValueState])

  const theme = useTheme()

  const userAgent = navigator.userAgent || navigator.vendor

  const isiOSMobile =
    useMediaQuery(theme.breakpoints.down('xs')) &&
    /iPad|iPhone|iPod/.test(userAgent) &&
    !window.MSStream

  return (
    <>
      <StyledAutocomplete
        open={isOpen}
        onOpen={handleOpen(true)}
        onClose={handleOpen(false)}
        value={selectedValue}
        inputValue={inputValue || inputValueState}
        renderOption={renderOption || renderOptionCallback}
        getOptionLabel={getOptionLabel}
        getOptionSelected={getOptionSelected}
        options={options}
        fullWidth
        disableClearable
        renderInput={renderInput}
        noOptionsText={noOptionsText}
        maxheight={!isiOSMobile ? '35vh' : '170px'} // calculated for most used iPhone resolution
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={changeHandler as any} // Material UI types are not cool here :/
        onBlur={blurHandler}
        onFocus={focusHandler}
        onInputChange={inputChangeHandler}
        id={name}
        getOptionDisabled={(option: Choice<T>) =>
          (option.value as unknown as string) === SEPARATOR_VALUE
        }
        dependson={dependson}
        autoHighlight={autoHighlight}
        className={className}
        filterOptions={filterOptions}
        freeSolo={freeSolo}
        loading={loading}
        loadingText={loadingText}
        autoComplete={autoComplete}
      />
      {tooltip && !showTooltipOnFieldClick && (
        <Tooltip open={isTooltipDisplayed}>{tooltip}</Tooltip>
      )}
    </>
  )
}

export default memo(
  Select,
  (prevProps, nextProps) =>
    JSON.stringify(prevProps) === JSON.stringify(nextProps)
) as typeof Select
